import { useState } from "react";

interface Community {
  name: string;
  route: string;
}

interface Subregion {
  name: string;
  description: string;
  communities: Community[];
  hub: { name: string; route: string };
}

interface Region {
  id: string;
  name: string;
  icon: string;
  subregions: Subregion[];
}

const regions: Region[] = [
  {
    id: "dallas-ft-worth",
    name: "DFW Metroplex",
    icon: "ri-building-line",
    subregions: [
      {
        name: "Dallas Core & Uptown",
        description: "Downtown Dallas, Uptown, Oak Lawn, Deep Ellum, and Knox-Henderson — all within the 635 loop and served from the Dallas hub. Uber/Lyft access to DFW International takes 30–45 minutes.",
        hub: { name: "Dallas", route: "/cities/dallas" },
        communities: [
          { name: "University Park", route: "/cities/dallas" }, { name: "Highland Park", route: "/cities/dallas" },
          { name: "Addison", route: "/cities/dallas" }, { name: "Farmers Branch", route: "/cities/dallas" },
          { name: "Coppell", route: "/cities/dallas" }, { name: "Grapevine", route: "/cities/dallas" },
          { name: "Las Colinas", route: "/cities/dallas" }, { name: "Mesquite", route: "/cities/dallas" },
          { name: "Balch Springs", route: "/cities/dallas" }, { name: "Sunnyvale", route: "/cities/dallas" },
        ],
      },
      {
        name: "North Dallas Suburbs",
        description: "Collin County tech corridor — Plano, Allen, McKinney, and Frisco. All cities are 30–45 minutes north of Downtown Dallas on US-75 or the Dallas North Tollway.",
        hub: { name: "Plano", route: "/cities/plano" },
        communities: [
          { name: "Allen", route: "/cities/plano" }, { name: "Wylie", route: "/cities/plano" },
          { name: "Murphy", route: "/cities/plano" }, { name: "Prosper", route: "/cities/plano" },
          { name: "Celina", route: "/cities/plano" }, { name: "Melissa", route: "/cities/plano" },
          { name: "Lucas", route: "/cities/plano" }, { name: "Parker", route: "/cities/plano" },
          { name: "Sachse", route: "/cities/plano" }, { name: "Rowlett", route: "/cities/plano" },
        ],
      },
      {
        name: "Mid-Cities & Denton County",
        description: "HEB area, Euless, Bedford, and Denton County suburbs along SH-121. DFW Airport is literally within this corridor — critical for airport-adjacent passport needs.",
        hub: { name: "Fort Worth", route: "/cities/fort-worth" },
        communities: [
          { name: "Euless", route: "/cities/fort-worth" }, { name: "Bedford", route: "/cities/fort-worth" },
          { name: "Hurst", route: "/cities/fort-worth" }, { name: "Colleyville", route: "/cities/fort-worth" },
          { name: "Southlake", route: "/cities/fort-worth" }, { name: "Flower Mound", route: "/cities/fort-worth" },
          { name: "Lewisville", route: "/cities/fort-worth" }, { name: "Little Elm", route: "/cities/fort-worth" },
          { name: "The Colony", route: "/cities/fort-worth" }, { name: "Corinth", route: "/cities/fort-worth" },
        ],
      },
      {
        name: "Southwest Tarrant & Johnson County",
        description: "Arlington, Mansfield, Burleson, and Cleburne — south of I-20 on US-287. Johnson County travelers often route through the Fort Worth hub for fastest service.",
        hub: { name: "Arlington", route: "/cities/arlington" },
        communities: [
          { name: "Mansfield", route: "/cities/arlington" }, { name: "Kennedale", route: "/cities/arlington" },
          { name: "Burleson", route: "/cities/arlington" }, { name: "Cleburne", route: "/cities/arlington" },
          { name: "Alvarado", route: "/cities/arlington" }, { name: "Grand Prairie", route: "/cities/arlington" },
          { name: "Midlothian", route: "/cities/arlington" }, { name: "Waxahachie", route: "/cities/arlington" },
          { name: "Cedar Hill", route: "/cities/arlington" }, { name: "Duncanville", route: "/cities/arlington" },
        ],
      },
    ],
  },
  {
    id: "houston-metro",
    name: "Houston Metro",
    icon: "ri-map-pin-2-line",
    subregions: [
      {
        name: "Houston Inner Loop",
        description: "Midtown, Montrose, Heights, Medical Center, and Galleria — all reachable within 30 minutes of Downtown Houston. IAH is about 25 miles north on I-45; Hobby is 10 miles southeast.",
        hub: { name: "Houston", route: "/cities/houston" },
        communities: [
          { name: "Bellaire", route: "/cities/houston" }, { name: "West University Place", route: "/cities/houston" },
          { name: "Pearland", route: "/cities/houston" }, { name: "Missouri City", route: "/cities/houston" },
          { name: "Sugar Land", route: "/cities/houston" }, { name: "Richmond", route: "/cities/houston" },
          { name: "Stafford", route: "/cities/houston" }, { name: "Jersey Village", route: "/cities/houston" },
          { name: "Humble", route: "/cities/houston" }, { name: "Atascocita", route: "/cities/houston" },
        ],
      },
      {
        name: "Northwest Houston & Katy",
        description: "Katy and its master-planned communities sit 30 miles west on I-10. Cy-Fair, Spring, and Klein are served by The Woodlands or Houston hubs depending on direction of travel.",
        hub: { name: "Houston", route: "/cities/houston" },
        communities: [
          { name: "Katy", route: "/cities/houston" }, { name: "Cypress", route: "/cities/houston" },
          { name: "Spring", route: "/cities/houston" }, { name: "Klein", route: "/cities/houston" },
          { name: "Tomball", route: "/cities/houston" }, { name: "Magnolia", route: "/cities/houston" },
          { name: "The Woodlands", route: "/cities/houston" }, { name: "Conroe", route: "/cities/houston" },
          { name: "Friendswood", route: "/cities/houston" }, { name: "League City", route: "/cities/houston" },
        ],
      },
      {
        name: "Southeast Houston & Galveston County",
        description: "Pasadena, Baytown, and Galveston Island — Port of Galveston is a major cruise embarkation port, making expedited passport service critical for last-minute cruise prep.",
        hub: { name: "Houston", route: "/cities/houston" },
        communities: [
          { name: "Galveston", route: "/cities/houston" }, { name: "Texas City", route: "/cities/houston" },
          { name: "La Marque", route: "/cities/houston" }, { name: "League City", route: "/cities/houston" },
          { name: "Baytown", route: "/cities/houston" }, { name: "La Porte", route: "/cities/houston" },
          { name: "Deer Park", route: "/cities/houston" }, { name: "South Houston", route: "/cities/houston" },
          { name: "Alvin", route: "/cities/houston" }, { name: "Manvel", route: "/cities/houston" },
        ],
      },
    ],
  },
  {
    id: "austin-san-antonio",
    name: "Austin & San Antonio Corridor",
    icon: "ri-landscape-line",
    subregions: [
      {
        name: "Austin Metro",
        description: "Travis County and its suburbs form Central Texas's largest metro. Austin-Bergstrom International (ABIA) is on the southeast side. Round Rock, Cedar Park, and Georgetown are 20–30 minutes north on I-35.",
        hub: { name: "Austin", route: "/cities/austin" },
        communities: [
          { name: "Round Rock", route: "/cities/austin" }, { name: "Cedar Park", route: "/cities/austin" },
          { name: "Georgetown", route: "/cities/austin" }, { name: "Leander", route: "/cities/austin" },
          { name: "Pflugerville", route: "/cities/austin" }, { name: "Hutto", route: "/cities/austin" },
          { name: "Kyle", route: "/cities/austin" }, { name: "Buda", route: "/cities/austin" },
          { name: "Bastrop", route: "/cities/austin" }, { name: "Manor", route: "/cities/austin" },
        ],
      },
      {
        name: "San Antonio & Bexar County",
        description: "San Antonio International Airport (SAT) is 10 minutes north of Downtown. Schertz, Converse, and Universal City cluster northeast off I-35; New Braunfels is 30 minutes north.",
        hub: { name: "San Antonio", route: "/cities/san-antonio" },
        communities: [
          { name: "New Braunfels", route: "/cities/san-antonio" }, { name: "Schertz", route: "/cities/san-antonio" },
          { name: "Cibolo", route: "/cities/san-antonio" }, { name: "Universal City", route: "/cities/san-antonio" },
          { name: "Converse", route: "/cities/san-antonio" }, { name: "Helotes", route: "/cities/san-antonio" },
          { name: "Leon Valley", route: "/cities/san-antonio" }, { name: "Boerne", route: "/cities/san-antonio" },
          { name: "Seguin", route: "/cities/san-antonio" }, { name: "Floresville", route: "/cities/san-antonio" },
        ],
      },
      {
        name: "I-35 Corridor — Killeen to San Marcos",
        description: "The mid-Texas I-35 corridor includes Waco, Killeen (Fort Cavazos), Temple, and San Marcos. Killeen-Fort Hood Regional Airport serves the large military community needing fast passport processing.",
        hub: { name: "Waco", route: "/cities/waco" },
        communities: [
          { name: "Killeen", route: "/cities/killeen" }, { name: "Copperas Cove", route: "/cities/killeen" },
          { name: "Harker Heights", route: "/cities/killeen" }, { name: "Temple", route: "/cities/waco" },
          { name: "Belton", route: "/cities/waco" }, { name: "Hewitt", route: "/cities/waco" },
          { name: "Woodway", route: "/cities/waco" }, { name: "San Marcos", route: "/cities/austin" },
          { name: "Wimberley", route: "/cities/austin" }, { name: "Lockhart", route: "/cities/austin" },
        ],
      },
    ],
  },
  {
    id: "west-texas",
    name: "West Texas & El Paso",
    icon: "ri-sun-line",
    subregions: [
      {
        name: "El Paso & Borderland",
        description: "El Paso International Airport is one of the closest US airports to Mexico. Socorro, Horizon City, and Anthony span both sides of the New Mexico state line. Cross-border travel makes passport urgency especially high here.",
        hub: { name: "El Paso", route: "/cities/el-paso" },
        communities: [
          { name: "Horizon City", route: "/cities/el-paso" }, { name: "Socorro", route: "/cities/el-paso" },
          { name: "Anthony", route: "/cities/el-paso" }, { name: "Fabens", route: "/cities/el-paso" },
          { name: "Clint", route: "/cities/el-paso" }, { name: "Fort Bliss", route: "/cities/el-paso" },
          { name: "Las Cruces NM area", route: "/cities/el-paso" }, { name: "Santa Teresa NM", route: "/cities/el-paso" },
        ],
      },
      {
        name: "Permian Basin — Midland & Odessa",
        description: "The Permian Basin is an energy hub with frequent international travel needs. Midland International Air & Space Port serves both cities; direct flights to international destinations are common for oil industry professionals.",
        hub: { name: "Midland", route: "/cities/midland" },
        communities: [
          { name: "Stanton", route: "/cities/midland" }, { name: "Andrews", route: "/cities/midland" },
          { name: "Crane", route: "/cities/midland" }, { name: "Monahans", route: "/cities/midland" },
          { name: "Pecos", route: "/cities/odessa" }, { name: "Fort Stockton", route: "/cities/odessa" },
          { name: "Big Spring", route: "/cities/midland" }, { name: "Snyder", route: "/cities/midland" },
        ],
      },
    ],
  },
  {
    id: "south-gulf",
    name: "South Texas & Gulf Coast",
    icon: "ri-ship-line",
    subregions: [
      {
        name: "Rio Grande Valley",
        description: "McAllen, Edinburg, Harlingen, and Brownsville form the densely populated RGV. The Anzalduas and Pharr-Reynosa bridges mean frequent cross-border travel — and frequent passport urgency — for valley residents.",
        hub: { name: "McAllen", route: "/cities/mcallen" },
        communities: [
          { name: "Edinburg", route: "/cities/mcallen" }, { name: "Mission", route: "/cities/mcallen" },
          { name: "Pharr", route: "/cities/mcallen" }, { name: "San Juan", route: "/cities/mcallen" },
          { name: "Weslaco", route: "/cities/mcallen" }, { name: "Harlingen", route: "/cities/mcallen" },
          { name: "Los Fresnos", route: "/cities/brownsville" }, { name: "San Benito", route: "/cities/brownsville" },
          { name: "Donna", route: "/cities/mcallen" }, { name: "Alamo", route: "/cities/mcallen" },
        ],
      },
      {
        name: "Corpus Christi & Coastal Bend",
        description: "Corpus Christi Bay communities, Portland, and the Padre Island area. Port of Corpus Christi sees significant international shipping. Naval Air Station Corpus Christi means a steady stream of military passport applications.",
        hub: { name: "Corpus Christi", route: "/cities/corpus-christi" },
        communities: [
          { name: "Portland", route: "/cities/corpus-christi" }, { name: "Robstown", route: "/cities/corpus-christi" },
          { name: "Flour Bluff", route: "/cities/corpus-christi" }, { name: "Aransas Pass", route: "/cities/corpus-christi" },
          { name: "Ingleside", route: "/cities/corpus-christi" }, { name: "Rockport", route: "/cities/corpus-christi" },
          { name: "Victoria", route: "/cities/corpus-christi" }, { name: "Laredo", route: "/cities/laredo" },
        ],
      },
    ],
  },
  {
    id: "east-north-texas",
    name: "East & North Texas",
    icon: "ri-tree-line",
    subregions: [
      {
        name: "East Texas Piney Woods",
        description: "Tyler is the hub of the Piney Woods region — Longview, Marshall, and Lufkin all route through here. East Texas Regional Airport offers connections, but most travelers drive to DFW or IAH for international departures.",
        hub: { name: "Tyler", route: "/cities/tyler" },
        communities: [
          { name: "Longview", route: "/cities/tyler" }, { name: "Whitehouse", route: "/cities/tyler" },
          { name: "Lindale", route: "/cities/tyler" }, { name: "Marshall", route: "/cities/tyler" },
          { name: "Nacogdoches", route: "/cities/tyler" }, { name: "Lufkin", route: "/cities/tyler" },
          { name: "Kilgore", route: "/cities/tyler" }, { name: "Henderson", route: "/cities/tyler" },
        ],
      },
      {
        name: "North Texas — Wichita Falls & Abilene",
        description: "Northwestern Texas cities served from Wichita Falls and Abilene hubs. Sheppard AFB (Wichita Falls) and Dyess AFB (Abilene) mean substantial active-duty populations needing expedited military passport processing.",
        hub: { name: "Wichita Falls", route: "/cities/wichita-falls" },
        communities: [
          { name: "Burkburnett", route: "/cities/wichita-falls" }, { name: "Iowa Park", route: "/cities/wichita-falls" },
          { name: "Bowie", route: "/cities/wichita-falls" }, { name: "Vernon", route: "/cities/wichita-falls" },
          { name: "Sweetwater", route: "/cities/abilene" }, { name: "San Angelo", route: "/cities/san-angelo" },
          { name: "Stephenville", route: "/cities/abilene" }, { name: "Mineral Wells", route: "/cities/fort-worth" },
        ],
      },
    ],
  },
];

export default function TexasCommunitiesWeServe() {
  const [activeRegion, setActiveRegion] = useState(regions[0].id);

  const current = regions.find((r) => r.id === activeRegion) || regions[0];

  return (
    <section id="communities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Statewide Coverage
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Communities We Serve Across Texas
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Don&apos;t see your city in the directory above? You&apos;re still covered. Every Texas community below is served through our nearest hub — with the same fast, reliable expedited passport processing.
          </p>
        </div>

        {/* Region tab selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {regions.map((region) => (
            <button
              key={region.id}
              onClick={() => setActiveRegion(region.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeRegion === region.id
                  ? "bg-[#1A365D] text-white"
                  : "bg-[#EEF2F8] text-[#1A365D] hover:bg-[#dce5f3]"
              }`}
            >
              <i className={`${region.icon} text-base`}></i>
              {region.name}
            </button>
          ))}
        </div>

        {/* Subregion cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {current.subregions.map((sub, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-100">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="font-bold text-[#1A365D] text-base">{sub.name}</h3>
                {sub.hub.route !== "#" ? (
                  <a
                    href={sub.hub.route}
                    className="flex-shrink-0 inline-flex items-center gap-1 bg-[#1A365D] text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-[#F6C344] hover:text-[#1A365D] transition-all whitespace-nowrap"
                  >
                    Hub: {sub.hub.name} <i className="ri-arrow-right-line"></i>
                  </a>
                ) : (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 bg-[#1A365D] text-white text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                    Hub: {sub.hub.name}
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">{sub.description}</p>
              <div className="flex flex-wrap gap-2">
                {sub.communities.map((c, j) => (
                  c.route !== "#" ? (
                    <a
                      key={j}
                      href={c.route}
                      className="inline-flex items-center text-xs font-medium text-[#1A365D] bg-white border border-gray-200 rounded-full px-3 py-1 hover:border-[#F6C344] hover:text-[#1A365D] transition-all whitespace-nowrap cursor-pointer"
                    >
                      {c.name}
                    </a>
                  ) : (
                    <span
                      key={j}
                      className="inline-flex items-center text-xs font-medium text-[#6B7280] bg-white border border-gray-200 rounded-full px-3 py-1 whitespace-nowrap"
                    >
                      {c.name}
                    </span>
                  )
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-400 text-sm mt-8">
          Not finding your exact community? <a href="tel:8772530084" className="text-[#1A365D] font-semibold hover:text-[#F6C344] transition-colors">Call us at 877-253-0084</a> — we serve all of Texas.
        </p>
      </div>
    </section>
  );
}